/** Binary sizes (matches typical file.size in browsers). */
export const MAX_COURSE_VIDEO_BYTES = 10 * 1024 * 1024;
export const MAX_COURSE_DOCUMENT_BYTES = 2 * 1024 * 1024;

export function formatSizeMb(bytes) {
  return (bytes / (1024 * 1024)).toFixed(1);
}

/**
 * @param {File | null | undefined} file
 * @returns {string | null} Error message or null if OK / no file
 */
export function validateCourseVideoFile(file) {
  if (!file) return null;
  if (file.size > MAX_COURSE_VIDEO_BYTES) {
    return `Video must be at most 10 MB (this file is ${formatSizeMb(file.size)} MB).`;
  }
  return null;
}

/**
 * @param {File} file
 * @returns {boolean}
 */
export function isCourseDocumentPdfFile(file) {
  if (!file) return false;
  if (!/\.pdf$/i.test(file.name)) return false;
  const t = (file.type || "").toLowerCase();
  if (!t) return true;
  if (t === "application/pdf" || t === "application/x-pdf") return true;
  if (t === "application/octet-stream") return true;
  return false;
}

/**
 * @param {File[]} files
 * @returns {{ valid: { name: string, file: File }[], errors: string[] }}
 */
export function validateCourseDocumentFiles(files) {
  const valid = [];
  const errors = [];
  files.forEach((file) => {
    if (!isCourseDocumentPdfFile(file)) {
      errors.push(`"${file.name}" is not a PDF. Only PDF files are allowed.`);
      return;
    }
    if (file.size > MAX_COURSE_DOCUMENT_BYTES) {
      errors.push(
        `"${file.name}" exceeds 2 MB limit (${formatSizeMb(file.size)} MB).`
      );
    } else {
      valid.push({ name: file.name, file });
    }
  });
  return { valid, errors };
}

/**
 * Run before submit (add or edit) when including optional video and new documents.
 * @param {File | null | undefined} videoFile
 * @param {{ name: string, file: File }[]} newDocuments
 * @returns {string | null}
 */
export function validateCourseUploadsForSubmit(videoFile, newDocuments) {
  const videoErr = validateCourseVideoFile(videoFile);
  if (videoErr) return videoErr;
  for (const doc of newDocuments) {
    if (!isCourseDocumentPdfFile(doc.file)) {
      return `Document "${doc.name}" must be a PDF file.`;
    }
    if (doc.file.size > MAX_COURSE_DOCUMENT_BYTES) {
      return `Document "${doc.name}" must be at most 2 MB (${formatSizeMb(
        doc.file.size
      )} MB).`;
    }
  }
  return null;
}
